import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/components/AuthContext";

const formatError = (detail) => {
  if (!detail) return "Sign in failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg || JSON.stringify(item)).join(" ");
  return detail.msg || String(detail);
};

export default function StaffLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("khalid@egaalservices.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/staff" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      navigate("/staff");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-brand-navy px-5 py-12 text-white" data-testid="staff-login-page">
      <form onSubmit={submit} className="w-full max-w-md rounded-lg border border-white/10 bg-white/[0.06] p-8 shadow-editorial backdrop-blur-xl" data-testid="staff-login-form">
        <div className="mb-8 flex items-center gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-lg bg-brand-copper text-brand-navy">
            <LockKeyhole className="h-6 w-6" />
          </span>
          <div>
            <p className="overline text-brand-copper">Staff Access</p>
            <h1 className="mt-1 font-serif text-3xl font-bold">Egaal Intake Dashboard</h1>
          </div>
        </div>
        {error && <div className="mb-5 rounded-lg border border-red-300/20 bg-red-500/10 p-3 text-sm font-bold text-red-100" data-testid="staff-login-error">{error}</div>}
        <div className="space-y-5">
          <div>
            <Label htmlFor="staff-email" className="text-white/80">Email</Label>
            <Input id="staff-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-email-input" className="mt-2 h-12 border-white/15 bg-slate-800 text-white" />
          </div>
          <div>
            <Label htmlFor="staff-password" className="text-white/80">Password</Label>
            <Input id="staff-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="staff-password-input" className="mt-2 h-12 border-white/15 bg-slate-800 text-white" />
          </div>
        </div>
        <Button type="submit" disabled={submitting} data-testid="staff-login-submit-button" className="mt-7 h-12 w-full rounded-full bg-brand-copper text-brand-navy hover:bg-brand-copper/90">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {submitting ? "Signing In…" : "Sign In"}
        </Button>
      </form>
    </main>
  );
}
